import { useEffect } from "react";
import type { ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { setAuthToken } from "./authToken";

interface AuthGateProps {
  children: ReactNode;
}

/**
 * Renders its children only once there's a valid Cognito session, redirecting visitors to the
 * Hosted UI. Mirrors the ID token into authToken during render, before any route loader fires.
 */
export const AuthGate = (props: AuthGateProps) => {
  const { children } = props;
  const auth = useAuth();
  const needsLogin =
    !auth.isLoading && !auth.isAuthenticated && !auth.error && !auth.activeNavigator;

  setAuthToken(auth.isAuthenticated ? auth.user?.id_token : undefined);

  useEffect(() => {
    if (needsLogin) {
      auth.signinRedirect();
    }
  }, [needsLogin, auth]);

  if (auth.error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Typography color="error">
          Sign-in failed: {auth.error.message}
        </Typography>
        <Button
          variant="contained"
          onClick={() => auth.signinRedirect()}
        >
          Try again
        </Button>
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};
